import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import './wikihousestyle.css';
import './WikiLayout.css';
import logo from '../Media/logo.png';
import WikiHome from './WikiHome';
import WikiItemDetail from './WikiItemDetail';
import WikiItemList from './WikiItemList';
import WikiMobList from './WikiMobList';
import WikiMobDetail from './WikiMobDetail';
import WikiSetDetail from './WikiSetDetail';
import { PageWrapper, FadeIn } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';
import api from '../API/Api';

function WikiNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const handleSearch = (e) => {
    const val = e.target.value;
    let path = location.pathname;
    
    // If the user is viewing a specific item and starts searching, take them back to the browse page or wiki root
    // so they can actually see the search results.
    if (path.includes('/item/')) {
      path = '/wiki/items';
    }

    if (val) {
      navigate(`${path}?q=${encodeURIComponent(val)}`, { replace: true });
    } else {
      navigate(path, { replace: true });
    }
  };

  return (
    <>
      <FadeIn y={-20} duration={0.6} className="wiki-nav">
        <div className="wiki-nav-brand" onClick={() => navigate('/wiki')}>
          <img src={logo} alt="SimpleMC" className="wiki-nav-logo" />
          <span className="wiki-nav-badge">Wiki</span>
        </div>

        <div className="wiki-nav-search-wrap">
          <span className="wiki-nav-search-icon"><McIcon materialKey="spyglass" size={16} /></span>
          <input
            className="wiki-nav-search"
            type="text"
            placeholder="Search items, mobs…"
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>

        <div className="wiki-nav-links">
          <NavLink to="/wiki" end className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Home
          </NavLink>
          <NavLink to="/wiki/items" className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Browse Items
          </NavLink>
          <NavLink to="/wiki/mobs" className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Browse Mobs
          </NavLink>
        </div>

        <Link to="/" className="wiki-nav-back">
          ← Back to SimpleMC
        </Link>

        <button className="wiki-nav-hamburger" onClick={() => setMenuOpen(o => !o)}>
          <span /><span /><span />
        </button>
      </FadeIn>

      {menuOpen && (
        <div className="wiki-mobile-drawer">
          <div className="wiki-mobile-search-wrap">
            <input
              className="wiki-nav-search"
              type="text"
              placeholder="Search items, mobs…"
              value={searchQuery}
              onChange={handleSearch}
              autoFocus
            />
          </div>
          <NavLink
            to="/wiki" end
            className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Home
          </NavLink>
          <NavLink
            to="/wiki/items"
            className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Browse Items
          </NavLink>
          <NavLink
            to="/wiki/mobs"
            className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            Browse Mobs
          </NavLink>
          <span className="wiki-mobile-link wiki-mobile-link--soon">Guides (coming soon)</span>
          <div className="wiki-mobile-divider" />
          <Link
            to="/"
            className="wiki-mobile-link"
            onClick={() => setMenuOpen(false)}
          >
            ← Back to SimpleMC
          </Link>
        </div>
      )}
    </>
  );
}

function WikiFooter() {
  return (
    <footer className="wiki-footer">
      <div className="wiki-footer-brand">Simple<em>MC</em> Wiki</div>
      <p>simplemcnetwork.net · Community Wiki · Season 1</p>
    </footer>
  );
}

export default function Wiki() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [mobs, setMobs] = useState([]);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [itemsRes, mobsRes, setsRes] = await Promise.all([
          api('getwikiitems'),
          api('getwikimobs'),
          api('getwikisets'),
        ]);

        if (itemsRes.isSuccess) setItems(itemsRes.data || []);
        if (mobsRes.isSuccess) setMobs(mobsRes.data || []);
        if (setsRes.isSuccess) setSets(setsRes.data || []);
      } catch (err) {
        console.error("Failed to load wiki data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div className="wiki-root">
      <WikiNav />
      <main>
        {loading ? (
          <div style={{ padding: '8rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading Wiki Database...
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route index element={<PageWrapper><WikiHome items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="items" element={<PageWrapper><WikiItemList items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="mobs" element={<PageWrapper><WikiMobList items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="browse" element={<Navigate to="/wiki/mobs" replace />} />
              <Route path="item/:slug" element={<PageWrapper><WikiItemDetail items={items} mobs={mobs} sets={sets} /></PageWrapper>} />
              <Route path="mob/:slug" element={<PageWrapper><WikiMobDetail items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="set/:slug" element={<PageWrapper><WikiSetDetail sets={sets} items={items} /></PageWrapper>} />
              <Route path="*" element={<PageWrapper><WikiHome items={items} mobs={mobs} /></PageWrapper>} />
            </Routes>
          </AnimatePresence>
        )}
      </main>
      <WikiFooter />
    </div>
  );
}
